import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Box, Card, CircularProgress, styled, TablePagination} from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import ProfileStatus, {BADGE_SX, BADGE_TEXT_SX, onRollStatus, statusColour} from './ProfileStatus';
import PropTypes from 'prop-types';
import {incompleteSectionList} from './incompleteSections';
import Tooltip from '@mui/material/Tooltip';
import PROFILE_TYPE from '../../../../constants/profileTypes';
import orange from '@mui/material/colors/orange';
import red from '@mui/material/colors/red';
import grey from '@mui/material/colors/grey';
import {STAFF_ROLES} from '../../StaffProfile/staffRoles';
import {CARD_GAP, CARD_HEIGHT, cardGrid, cardsTop, pageTop, cardsForChoice, cardsOnScreen, choiceForRows, PAGE_MULTIPLES, rowChoices, rowsLabel, screenBottom, windowResized, windowSize} from './cardsOnScreen';
import useUser from '../../../../utility/hooks/useUser';
import {parentAccountsLine} from './parentAccountTooltip';
// Maria, 19 October 2026: the first page shows as many cards as fit between the top of the first card and the line
// with 'Admicity © 2026' at the foot of the page, and the page numbers are on that line. A larger page can be chosen,
// two or four screens of cards, always whole rows, and the page then scrolls down.
export const PROFILE_PAGE_SIZE = 20;

export {cardsOnScreen, PAGE_MULTIPLES};
// the page numbers, only as tall as the line with 'Admicity © 2026'
const PAGINATION_ON_FOOTER_LINE = {
    flexShrink: 0,
    position: 'relative',
    height: 24,
    overflow: 'visible',
    '& .MuiToolbar-root': {minHeight: 24, height: 24},
    '& .MuiIconButton-root': {p: 0},
    '& .MuiInputBase-root': {height: 24},
    '& .MuiTablePagination-select': {py: 0, minHeight: 0, lineHeight: '24px'}
};
// Maria, 27 October 2026: each person's page size is remembered in their browser for the next time they sign in
const pageSizeKey = userId => `admicity.profilesPageSize.${userId || 'anyone'}`;
const readChoice = userId => {
    try {
        return window.localStorage.getItem(pageSizeKey(userId)) || '1';
    } catch (e) {
        return '1';
    }
};
const keepChoice = (userId, choice) => {
    try {
        window.localStorage.setItem(pageSizeKey(userId), choice);
    } catch (e) {
        // the choice is kept until the page is reloaded
    }
};

const ProfileCard = styled(Card)(() => ({
    // Maria, 19 October 2026: smaller cards with less padding, sharing the width of the page
    width: '100%',
    boxSizing: 'border-box',
    // Maria, 19 October 2026: shorter cards, just tall enough for the status, the icon and the two names
    height: `${CARD_HEIGHT}px`,
    // Maria, 19 October 2026: the same 4px around the photo, between the photo and the status, and to the right of the status
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    // the name starts 8px from the photo
    justifyContent: 'flex-start',
    gap: '4px',
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e0e0e0',
}));

const ProfileInfoBox = styled(Box)({
    width: '100%',
    height: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
});

// Maria, 20 October 2026: the hover on the orange mark lists the sections still to confirm
// AD-2398: the parent accounts that count for the icon, as a quieter line under the message
const ParentAccounts = ({profile}) => {
    const line = parentAccountsLine(profile);
    return line ? <Typography variant="body2" sx={{mt: 0.75, color: 'text.secondary'}}>{line}</Typography> : null;
};

ParentAccounts.propTypes = {
    profile: PropTypes.object
};

const IncompleteSections = ({sections, profile}) => (
    <Box>
        <Typography variant="body2" fontWeight={600}>Incomplete Profile</Typography>
        {incompleteSectionList(sections).map(section => (
            <Typography key={section} variant="body2">{section}</Typography>
        ))}
        <ParentAccounts profile={profile}/>
    </Box>
);

IncompleteSections.propTypes = {
    sections: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    profile: PropTypes.object
};

const ProfilesGrid = (
    {
        data,
        onProfileSelect,
        handlePaginationModelChange,
        totalItemCount,
        pageSize,
        pageNumber,
        isLoading,
        noRowsOverlay,
        readonly
    }) => {
    const parentRef = useRef(null);
    const {user} = useUser();
    const [grid, setGrid] = useState(null);
    const choiceRef = useRef(null);
    // AD-2831: no re-measure while the page size list is open, as opening it hides the scroll bar
    const sizeListOpen = useRef(false);
    if (choiceRef.current === null) {
        choiceRef.current = readChoice(user?.id);
    }
    const [footerLine, setFooterLine] = useState(false);
    const paginationLineRef = useRef(null);
    const [paginationOffset, setPaginationOffset] = useState(0);
    // the page numbers are moved down onto the line with 'Admicity © 2026', centred on it
    useLayoutEffect(() => {
        const line = paginationLineRef.current;
        const footer = document.querySelector('[data-testid="app-footer"]');
        if (!footerLine || !line || !footer) {
            setPaginationOffset(0);
            return;
        }
        const footerBox = footer.getBoundingClientRect();
        setPaginationOffset(Math.round(footerBox.top + footerBox.height / 2 - 12 - line.getBoundingClientRect().top));
    });
    const fitToWindow = useCallback(() => {
        const node = parentRef.current;
        // AD-2831: not while the page size list is open or closing, as MUI locks the page's scroll then
        if (!node || sizeListOpen.current || getComputedStyle(document.body).overflow === 'hidden') return;
        const box = node.getBoundingClientRect();
        // on a tablet or phone the menu bar is fixed along the bottom of the window, so the cards end above it
        // Maria, 29 October 2026: Chrome on an iPhone gave too little height while its toolbars were showing, so the
        // bottom of the window is the larger of the window and the phone's visible area
        const viewport = window.visualViewport;
        const visibleBottom = Math.max(window.innerHeight, viewport ? Math.round(viewport.offsetTop + viewport.height) : 0);
        const bottomBar = (document.elementsFromPoint?.(box.left + 1, visibleBottom - 1) ?? [])
            .find(element => getComputedStyle(element).position === 'fixed');
        const windowBottom = bottomBar ? bottomBar.getBoundingClientRect().top : visibleBottom;
        // the cards end 8px above the line with 'Admicity © 2026', where it is in the window; otherwise above the page numbers
        const footer = document.querySelector('[data-testid="app-footer"]');
        const footerTop = footer?.offsetParent ? footer.getBoundingClientRect().top : null;
        const onFooterLine = footerTop !== null && footerTop <= windowBottom;
        const bottom = screenBottom(windowBottom, footer?.offsetParent ? footer.offsetHeight : 0);
        setFooterLine(onFooterLine);
        // AD-2831 (David, 30 October 2026): one screen is measured from where the cards start on the page, so rows scrolled
        // past are not counted as fitting on the screen, which asked for a bigger page and sent the page back to the top
        const fit = cardGrid(box.width - CARD_GAP, bottom - cardsTop(pageTop(node)) - 8);
        setGrid(fit);
        const wanted = cardsForChoice(choiceRef.current, fit);
        if (wanted !== pageSize) {
            // the first card on the page stays on the page shown
            handlePaginationModelChange(Math.floor((pageNumber * pageSize) / wanted), wanted);
        }
    }, [pageSize, pageNumber, handlePaginationModelChange]);
    // measured again once the cards have loaded, as the line with 'Admicity © 2026' moves while they load
    useLayoutEffect(fitToWindow, [fitToWindow, isLoading]);
    // Maria, 29 October 2026: on an iPhone the page once settled on one row, as Safari's address bar was still moving
    // when it measured. It is measured again once the page has settled, and whenever the phone's visible area changes
    // (Safari reports that on visualViewport, not on the window).
    useEffect(() => {
        const viewport = window.visualViewport;
        let last = windowSize(window);
        const onResize = () => {
            const now = windowSize(window);
            if (!windowResized(last, now)) return;
            last = now;
            fitToWindow();
        };
        window.addEventListener('resize', onResize);
        viewport?.addEventListener('resize', onResize);
        const settled = [window.setTimeout(fitToWindow, 300), window.setTimeout(fitToWindow, 1000)];
        return () => {
            window.removeEventListener('resize', onResize);
            viewport?.removeEventListener('resize', onResize);
            settled.forEach(window.clearTimeout);
        };
    }, [fitToWindow]);
    const paginationRef = useRef(null);

    const handleChangePage = (event, newPage) => {
        handlePaginationModelChange(newPage, pageSize);
    };

    const handleChangeRowsPerPage = (event) => {
        const cards = Number(event.target.value);
        if (grid) {
            choiceRef.current = choiceForRows(Math.round(cards / grid.columns), grid.rows);
            keepChoice(user?.id, choiceRef.current);
        }
        handlePaginationModelChange(0, cards);
    };

    const OverlayContainer = props => {
        let {children} = props;
        return <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
        >
            {children}
        </Box>;
    };

    OverlayContainer.propTypes = {children: PropTypes.any};

    const buildConfirmIcon = (profile) => {
        // Maria, 19 October 2026: an applicant's parents can only create an account, and so complete the profile, once the
        // place is Accepted, so the icon is shown only for Accepted or Admitted applicants and children on roll
        const parentsCanHaveAccount = profile.personType === PROFILE_TYPE.STUDENT ||
            ['Accepted', 'Admitted'].includes(profile.statusCategory);
        if (!parentsCanHaveAccount) {
            // the space is kept, so the names are in the same place on every card
            return <Box sx={{width: 15, height: 15}}/>;
        }
        // Maria, 19 October 2026: a grey icon on a child on roll while no parent has been sent an invitation
        if (profile.hasExistingContactAccount === false && profile.personType === PROFILE_TYPE.STUDENT && !profile.parentInvitationSent) {
            return <Tooltip title="Invitation not sent">
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: grey[500]}} data-testid="parent-invitation-not-sent"/>
            </Tooltip>;
        }
        if (profile.hasExistingContactAccount === false) {
            return <Tooltip title="No Parent Account">
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: red[500]}}/>
            </Tooltip>;
        }

        return !profile.needsConfirmation || profile.needsConfirmation.length === 0
            ? <Tooltip title={<Box><Typography variant="body2" fontWeight={600}>Profile Complete</Typography><ParentAccounts profile={profile}/></Box>}><CheckOutlinedIcon color="success" sx={{stroke: 'currentColor', strokeWidth: 1.5}}/></Tooltip>
            : <Tooltip title={<IncompleteSections sections={profile.needsConfirmation} profile={profile}/>}>
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: orange[500]}} data-testid="profile-incomplete"/>
            </Tooltip>
    }

    // Maria, 19 October 2026: a staff card shows the member of staff's role like an applicant's status, in greens that
    // grow darker with seniority, from a very light green for Employee to the green of the main menu bar for School Admin.
    // A red icon says 'Account not yet created' when an invitation has been sent and not used, and a grey one says
    // 'Invitation not sent', for example while employee invitations are switched off.
    const buildStaffBadge = (profile) => {
        const role = STAFF_ROLES[profile.accountRole] ?? STAFF_ROLES.Employee;
        return (
            <Box
                data-testid="staff-role-badge"
                // Maria, 20 October 2026: the same size and font as the student and applicant status badges (BADGE_SX)
                sx={{backgroundColor: role.background, color: role.text, fontWeight: 600, textAlign: 'center', ...BADGE_SX}}
            >
                <Typography noWrap title={role.title ?? role.label} variant="subtitle2" sx={BADGE_TEXT_SX}>{role.label}</Typography>
            </Box>
        );
    };

    const buildStaffIcon = (profile) => {
        if (profile.accountStatus === 'Invited') {
            return <Tooltip title="Account not yet created">
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: red[500]}} data-testid="staff-account-not-created"/>
            </Tooltip>;
        }
        if (profile.accountStatus === 'NotInvited') {
            return <Tooltip title="Invitation not sent">
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: grey[500]}} data-testid="staff-invitation-not-sent"/>
            </Tooltip>;
        }
        // the space is kept, so the names are in the same place on every card
        return <Box sx={{width: 15, height: 15}}/>;
    };

    const getProfileListGrid = () => {
        return data?.map((profile) => {
            const isEnquiry = profile.personType === PROFILE_TYPE.ENQUIRY;
            const isStaff = Boolean(profile.accountStatus);
            const badge = isEnquiry
                ? {status: 'Enquiry', statusCategory: 'Enquiry'}
                : profile.personType === PROFILE_TYPE.STUDENT
                    ? onRollStatus(profile.dateOfAdmission)
                    : {status: profile.status, statusCategory: profile.statusCategory};
            const profileStatus = isStaff
                ? buildStaffBadge(profile)
                : profile.personType === PROFILE_TYPE.APPLICATION || profile.personType === PROFILE_TYPE.STUDENT || isEnquiry
                    ? <ProfileStatus status={badge.status} statusCategory={badge.statusCategory}/>
                    : null;

            return (<Box key={profile.id} sx={{minWidth: 0}}>
                {/* Maria, 19 October 2026: a child the member of staff cannot open is not marked on the card; they are
                    told when they click on it */}
                    <ProfileCard
                        onClick={() => {
                            if (readonly) {
                                return;
                            }
                            onProfileSelect(profile);
                        }}
                        sx={readonly ? {cursor: 'default'} : {}}
                    >
                        <Avatar
                            variant="square"
                            sx={{
                                // Maria, 19 October 2026: the 3 by 4 shape of a school photo, so its sides are not cut off,
                                // as tall as the card allows
                                width: `${(CARD_HEIGHT - 10) * 0.75}px`,
                                height: `${CARD_HEIGHT - 10}px`,
                                alignSelf: 'flex-start',
                                borderRadius: 1,
                                // Maria, 19 October 2026: without a photo, the white head and shoulders have an outline in the
                                // colour of the badge, on a slightly lighter grey
                                backgroundColor: '#CFCFCF',
                                '& > svg': {
                                    stroke: isStaff
                                        ? (STAFF_ROLES[profile.accountRole] ?? STAFF_ROLES.Employee).background
                                        : statusColour(badge.statusCategory),
                                    strokeWidth: 0.8,
                                    paintOrder: 'stroke',
                                    overflow: 'visible'
                                }
                            }}
                            src={profile.photo ? `data:image/jpeg;base64,${profile.photo}` : undefined}
                        />
                        {/* Maria, 19 October 2026: the application status runs from the photo to the right of the card,
                            level with the top of the photo, with square corners except the top right, which is rounded like
                            the top left of the photo. The profile status icon is centred underneath it and the names
                            underneath that, with a little space between each. */}
                        <Box display={'flex'} flexDirection={'column'} justifyContent={'flex-start'} gap={'3px'} alignSelf={'stretch'} flexGrow={1} minWidth={0}>
                            <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={'3px'}
                                 pt={profileStatus ? 0 : '4px'}
                                 sx={{
                                     // Maria, 19 October 2026: a smaller icon, so the names sit higher
                                     '& .MuiSvgIcon-root': {fontSize: 15, display: 'block'},
                                     '& > div:first-of-type': profileStatus
                                         ? {width: '100%', boxSizing: 'border-box', borderRadius: '0 4px 0 0'}
                                         : {}
                                 }}>
                                        {profileStatus}
                                        {(profile.personType === PROFILE_TYPE.APPLICATION ||
                                            profile.personType === PROFILE_TYPE.STUDENT) && buildConfirmIcon(profile)}
                                        {isStaff && buildStaffIcon(profile)}
                            </Box>
                            <ProfileInfoBox sx={{height: 'auto', flexGrow: 0}}>
                                {/* Maria, 19 October 2026: the names centred under the status label and icon */}
                                <Box
                                    minWidth={0}
                                    textAlign={'center'}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        // Maria, 19 October 2026: the whole name, cut with '...' only when the card is too narrow
                                        noWrap
                                        lineHeight={1.2}
                                    >
                                        <span title={profile.name?.trim()}>{profile.name?.trim()}</span>
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        noWrap
                                        lineHeight={1.2}
                                    >
                                        <span title={profile.surname?.trim()}>{profile.surname?.trim()}</span>
                                    </Typography>
                                </Box>
                            </ProfileInfoBox>
                        </Box>
                    </ProfileCard>
            </Box>);
        });
    };

    const NoProfilesView = () => <OverlayContainer>
        {
            noRowsOverlay?.component
                ? noRowsOverlay?.component
                : <Typography variant="h6">No data</Typography>
        }
    </OverlayContainer>;

    return (
        <Box
            ref={parentRef}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1
            }}
        >
            {
                isLoading
                    ? <OverlayContainer>
                        <CircularProgress size={70}/>
                    </OverlayContainer>
                    : <>
                        {
                            data?.length === 0
                                ? <NoProfilesView/>
                                : <>
                                    {/* Maria, 19 October 2026: the cards run down the page without a scroll bar of their own,
                                        with the page numbers after the last card */}
                                    <Box sx={{paddingBottom: 1}}>
                                        {/* Maria, 19 October 2026: the cards fill the width up to the scroll bar, at least 160px
                                            each, 8px apart */}
                                        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1, pr: 1}}>
                                            {getProfileListGrid()}
                                        </Box>
                                    </Box>
                                    <Box ref={paginationLineRef} sx={footerLine ? {height: 0, overflow: 'visible', position: 'relative', zIndex: 1} : {}}>
                                    <TablePagination
                                        ref={paginationRef}
                                        sx={footerLine ? {...PAGINATION_ON_FOOTER_LINE, top: paginationOffset} : {flexShrink: 0, height: 52}}
                                        rowsPerPageOptions={grid
                                            ? rowChoices(grid.rows).map(rows => ({value: rows * grid.columns, label: rowsLabel(rows)}))
                                            : [pageSize]}
                                        labelRowsPerPage=""
                                        component="div"
                                        count={totalItemCount}
                                        rowsPerPage={pageSize}
                                        page={pageNumber}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        SelectProps={{onOpen: () => { sizeListOpen.current = true; }, onClose: () => { sizeListOpen.current = false; }}}
                                    />
                                    </Box>
                                </>
                        }
                    </>
            }
        </Box>
    );
};

ProfilesGrid.propTypes = {
    data: PropTypes.array,
    onProfileSelect: PropTypes.func,
    handlePaginationModelChange: PropTypes.func,
    totalItemCount: PropTypes.number,
    pageSize: PropTypes.number,
    pageNumber: PropTypes.number,
    isLoading: PropTypes.bool,
    noRowsOverlay: PropTypes.object,
    readonly: PropTypes.bool
};

export default ProfilesGrid;
